"use client";

// src/blocks/DashboardShell.tsx
import {
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@powercore/ui";
import { useState } from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
function DashboardShell({
  children,
  logo,
  navigation = [],
  user,
  headerActions,
  footer,
  sidebarCollapsed: controlledCollapsed,
  onSidebarToggle
}) {
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const collapsed = controlledCollapsed ?? internalCollapsed;
  const toggleSidebar = () => {
    const newValue = !collapsed;
    setInternalCollapsed(newValue);
    onSidebarToggle?.(newValue);
  };
  return /* @__PURE__ */ jsxs("div", { className: "pc-dashboard-shell", children: [
    /* @__PURE__ */ jsxs(
      "aside",
      {
        className: `pc-dashboard-sidebar ${collapsed ? "pc-dashboard-sidebar--collapsed" : ""}`,
        style: {
          width: collapsed ? "4rem" : "16rem",
          minHeight: "100vh",
          backgroundColor: "var(--pc-bg-base)",
          borderRight: "1px solid var(--pc-border)",
          display: "flex",
          flexDirection: "column",
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
          transition: "width var(--pc-transition-normal)",
          zIndex: 40
        },
        children: [
          /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                padding: "1rem",
                borderBottom: "1px solid var(--pc-border)",
                display: "flex",
                alignItems: "center",
                justifyContent: collapsed ? "center" : "space-between",
                minHeight: "4rem"
              },
              children: [
                !collapsed && logo,
                /* @__PURE__ */ jsx(
                  "button",
                  {
                    onClick: toggleSidebar,
                    style: {
                      padding: "0.5rem",
                      backgroundColor: "transparent",
                      border: "none",
                      cursor: "pointer",
                      color: "var(--pc-fg-muted)",
                      borderRadius: "var(--pc-radius-btn)"
                    },
                    "aria-label": collapsed ? "Expand sidebar" : "Collapse sidebar",
                    children: /* @__PURE__ */ jsx(MenuIcon, {})
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsx("nav", { style: { flex: 1, overflow: "auto", padding: "0.5rem" }, children: navigation.map((section, sectionIndex) => /* @__PURE__ */ jsxs("div", { style: { marginBottom: "1rem" }, children: [
            section.title && !collapsed && /* @__PURE__ */ jsx(
              "div",
              {
                style: {
                  padding: "0.5rem 0.75rem",
                  fontSize: "var(--pc-text-xs)",
                  fontWeight: "var(--pc-weight-semibold)",
                  color: "var(--pc-fg-subtle)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em"
                },
                children: section.title
              }
            ),
            section.items.map((item) => /* @__PURE__ */ jsx(NavItemButton, { item, collapsed }, item.id))
          ] }, sectionIndex)) }),
          footer && !collapsed && /* @__PURE__ */ jsx("div", { style: { padding: "1rem", borderTop: "1px solid var(--pc-border)" }, children: footer })
        ]
      }
    ),
    /* @__PURE__ */ jsxs(
      "div",
      {
        style: {
          marginLeft: collapsed ? "4rem" : "16rem",
          transition: "margin-left var(--pc-transition-normal)",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column"
        },
        children: [
          /* @__PURE__ */ jsxs(
            "header",
            {
              style: {
                height: "4rem",
                backgroundColor: "var(--pc-bg-base)",
                borderBottom: "1px solid var(--pc-border)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0 1.5rem",
                position: "sticky",
                top: 0,
                zIndex: 30
              },
              children: [
                /* @__PURE__ */ jsx("div", {}),
                /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: "1rem" }, children: [
                  headerActions,
                  user && /* @__PURE__ */ jsx(UserMenu, { user })
                ] })
              ]
            }
          ),
          /* @__PURE__ */ jsx(
            "main",
            {
              style: {
                flex: 1,
                padding: "var(--pc-space-page-y) var(--pc-space-page-x)",
                backgroundColor: "var(--pc-bg-subtle)"
              },
              children
            }
          )
        ]
      }
    )
  ] });
}
function NavItemButton({ item, collapsed }) {
  return /* @__PURE__ */ jsxs(
    "button",
    {
      onClick: item.onClick,
      style: {
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        padding: collapsed ? "0.75rem" : "0.5rem 0.75rem",
        justifyContent: collapsed ? "center" : "flex-start",
        backgroundColor: item.active ? "var(--pc-primary-subtle)" : "transparent",
        color: item.active ? "var(--pc-primary)" : "var(--pc-fg-base)",
        border: "none",
        borderRadius: "var(--pc-radius-btn)",
        cursor: "pointer",
        fontSize: "var(--pc-text-sm)",
        fontWeight: "var(--pc-weight-medium)",
        textAlign: "left",
        transition: "all var(--pc-transition-fast)"
      },
      children: [
        item.icon,
        !collapsed && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx("span", { style: { flex: 1 }, children: item.label }),
          item.badge && /* @__PURE__ */ jsx(Badge, { variant: "primary", style: { fontSize: "0.625rem" }, children: item.badge })
        ] })
      ]
    }
  );
}
function UserMenu({ user }) {
  return /* @__PURE__ */ jsxs(DropdownMenu, { children: [
    /* @__PURE__ */ jsxs(
      DropdownMenuTrigger,
      {
        style: {
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "0.25rem 0.5rem",
          backgroundColor: "transparent",
          border: "1px solid var(--pc-border)",
          borderRadius: "var(--pc-radius-full)",
          cursor: "pointer"
        },
        children: [
          /* @__PURE__ */ jsx(
            "div",
            {
              style: {
                width: "2rem",
                height: "2rem",
                borderRadius: "var(--pc-radius-full)",
                backgroundColor: "var(--pc-primary-subtle)",
                color: "var(--pc-primary)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "var(--pc-text-sm)",
                fontWeight: "var(--pc-weight-semibold)"
              },
              children: user.avatar ? /* @__PURE__ */ jsx(
                "img",
                {
                  src: user.avatar,
                  alt: user.name,
                  style: { width: "100%", height: "100%", borderRadius: "var(--pc-radius-full)" }
                }
              ) : user.name.charAt(0).toUpperCase()
            }
          ),
          /* @__PURE__ */ jsx(ChevronDownIcon, {})
        ]
      }
    ),
    /* @__PURE__ */ jsxs(DropdownMenuContent, { align: "end", children: [
      /* @__PURE__ */ jsxs("div", { style: { padding: "0.5rem 0.75rem", borderBottom: "1px solid var(--pc-border)" }, children: [
        /* @__PURE__ */ jsx("div", { style: { fontWeight: "var(--pc-weight-medium)", color: "var(--pc-fg-base)" }, children: user.name }),
        user.email && /* @__PURE__ */ jsx("div", { style: { fontSize: "var(--pc-text-xs)", color: "var(--pc-fg-muted)" }, children: user.email })
      ] }),
      user.menuItems?.map((menuItem, index) => /* @__PURE__ */ jsx(
        DropdownMenuItem,
        {
          onClick: menuItem.onClick,
          destructive: menuItem.destructive,
          children: menuItem.label
        },
        index
      ))
    ] })
  ] });
}
function MenuIcon() {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: "20",
      height: "20",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      children: /* @__PURE__ */ jsx("path", { d: "M3 12h18M3 6h18M3 18h18" })
    }
  );
}
function ChevronDownIcon() {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: "16",
      height: "16",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      children: /* @__PURE__ */ jsx("path", { d: "M6 9l6 6 6-6" })
    }
  );
}

// src/blocks/WorkflowShell.tsx
import { Badge as Badge2 } from "@powercore/ui";
import { useState as useState2, useEffect, useRef } from "react";
import { Fragment as Fragment2, jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
function WorkflowShell({
  children,
  logo,
  productName,
  contextName,
  contextLogo,
  workflowSteps = [],
  adminNavigation = [],
  user,
  headerActions,
  adminMode = false,
  onAdminModeToggle,
  showStepNumbers = false,
  showProgressConnector = true
}) {
  const [internalAdminMode, setInternalAdminMode] = useState2(false);
  const isAdminMode = adminMode || internalAdminMode;
  const toggleAdminMode = () => {
    const newValue = !isAdminMode;
    setInternalAdminMode(newValue);
    onAdminModeToggle?.(newValue);
  };
  const sidebarWidth = isAdminMode ? "14rem" : "0";
  return /* @__PURE__ */ jsxs2("div", { className: "pc-workflow-shell", style: { minHeight: "100vh" }, children: [
    /* @__PURE__ */ jsxs2(
      "header",
      {
        style: {
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: "4rem",
          backgroundColor: "var(--pc-bg-base)",
          borderBottom: "1px solid var(--pc-border)",
          display: "flex",
          alignItems: "center",
          padding: "0 1.5rem",
          zIndex: 50,
          gap: "1.5rem"
        },
        children: [
          /* @__PURE__ */ jsxs2(
            "div",
            {
              style: {
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                flexShrink: 0
              },
              children: [
                logo,
                productName && /* @__PURE__ */ jsx2(
                  "span",
                  {
                    style: {
                      fontWeight: "var(--pc-weight-semibold)",
                      fontSize: "var(--pc-text-lg)",
                      color: "var(--pc-fg-base)"
                    },
                    children: productName
                  }
                ),
                (contextName || contextLogo) && /* @__PURE__ */ jsxs2(Fragment2, { children: [
                  /* @__PURE__ */ jsx2(
                    "span",
                    {
                      style: {
                        color: "var(--pc-fg-subtle)",
                        fontSize: "var(--pc-text-lg)"
                      },
                      children: "/"
                    }
                  ),
                  contextLogo || /* @__PURE__ */ jsx2(
                    "span",
                    {
                      style: {
                        fontWeight: "var(--pc-weight-medium)",
                        fontSize: "var(--pc-text-base)",
                        color: "var(--pc-fg-muted)"
                      },
                      children: contextName
                    }
                  )
                ] })
              ]
            }
          ),
          !isAdminMode && /* @__PURE__ */ jsx2(
            "nav",
            {
              style: {
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
                flex: 1,
                justifyContent: "center",
                overflow: "auto"
              },
              children: workflowSteps.map((step, index) => /* @__PURE__ */ jsx2(
                WorkflowStepItem,
                {
                  step,
                  index,
                  showNumber: showStepNumbers,
                  showConnector: showProgressConnector && index < workflowSteps.length - 1,
                  isLast: index === workflowSteps.length - 1
                },
                step.id
              ))
            }
          ),
          isAdminMode && /* @__PURE__ */ jsx2("div", { style: { flex: 1, display: "flex", justifyContent: "center" }, children: /* @__PURE__ */ jsx2(
            "span",
            {
              style: {
                fontSize: "var(--pc-text-base)",
                fontWeight: "var(--pc-weight-medium)",
                color: "var(--pc-fg-muted)"
              },
              children: "Admin Dashboard"
            }
          ) }),
          /* @__PURE__ */ jsxs2(
            "div",
            {
              style: {
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                flexShrink: 0
              },
              children: [
                headerActions,
                user?.isAdmin && /* @__PURE__ */ jsxs2(
                  "button",
                  {
                    onClick: toggleAdminMode,
                    style: {
                      padding: "0.5rem 0.75rem",
                      backgroundColor: isAdminMode ? "var(--pc-primary-subtle)" : "transparent",
                      color: isAdminMode ? "var(--pc-primary)" : "var(--pc-fg-muted)",
                      border: "1px solid",
                      borderColor: isAdminMode ? "var(--pc-primary)" : "var(--pc-border)",
                      borderRadius: "var(--pc-radius-btn)",
                      cursor: "pointer",
                      fontSize: "var(--pc-text-sm)",
                      fontWeight: "var(--pc-weight-medium)",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.375rem",
                      transition: "all var(--pc-transition-fast)"
                    },
                    "aria-label": isAdminMode ? "Exit admin mode" : "Enter admin mode",
                    children: [
                      /* @__PURE__ */ jsx2(AdminIcon, {}),
                      /* @__PURE__ */ jsx2("span", { children: "Admin" })
                    ]
                  }
                ),
                user && /* @__PURE__ */ jsx2(WorkflowUserMenu, { user })
              ]
            }
          )
        ]
      }
    ),
    isAdminMode && /* @__PURE__ */ jsx2(
      "aside",
      {
        style: {
          position: "fixed",
          top: "4rem",
          left: 0,
          bottom: 0,
          width: sidebarWidth,
          backgroundColor: "var(--pc-bg-base)",
          borderRight: "1px solid var(--pc-border)",
          overflowY: "auto",
          padding: "0.5rem",
          zIndex: 40,
          transition: "width var(--pc-transition-normal)"
        },
        children: adminNavigation.map((section, sectionIndex) => /* @__PURE__ */ jsxs2("div", { style: { marginBottom: "1rem" }, children: [
          section.title && /* @__PURE__ */ jsx2(
            "div",
            {
              style: {
                padding: "0.5rem 0.75rem",
                fontSize: "var(--pc-text-xs)",
                fontWeight: "var(--pc-weight-semibold)",
                color: "var(--pc-fg-subtle)",
                textTransform: "uppercase",
                letterSpacing: "0.05em"
              },
              children: section.title
            }
          ),
          section.items.map((item) => /* @__PURE__ */ jsxs2(
            "button",
            {
              onClick: item.onClick,
              style: {
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                padding: "0.5rem 0.75rem",
                backgroundColor: item.active ? "var(--pc-primary-subtle)" : "transparent",
                color: item.active ? "var(--pc-primary)" : "var(--pc-fg-base)",
                border: "none",
                borderRadius: "var(--pc-radius-btn)",
                cursor: "pointer",
                fontSize: "var(--pc-text-sm)",
                fontWeight: "var(--pc-weight-medium)",
                textAlign: "left",
                transition: "all var(--pc-transition-fast)"
              },
              children: [
                item.icon,
                /* @__PURE__ */ jsx2("span", { children: item.label })
              ]
            },
            item.id
          ))
        ] }, sectionIndex))
      }
    ),
    /* @__PURE__ */ jsx2(
      "main",
      {
        style: {
          marginTop: "4rem",
          marginLeft: isAdminMode ? sidebarWidth : 0,
          minHeight: "calc(100vh - 4rem)",
          padding: "var(--pc-space-page-y) var(--pc-space-page-x)",
          backgroundColor: "var(--pc-bg-subtle)",
          transition: "margin-left var(--pc-transition-normal)"
        },
        children
      }
    )
  ] });
}
function WorkflowStepItem({
  step,
  index,
  showNumber,
  showConnector,
  isLast
}) {
  const isClickable = !step.disabled && step.onClick;
  return /* @__PURE__ */ jsxs2(Fragment2, { children: [
    /* @__PURE__ */ jsxs2(
      "button",
      {
        onClick: step.onClick,
        disabled: step.disabled,
        style: {
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "0.5rem 0.875rem",
          backgroundColor: step.active ? "var(--pc-primary-subtle)" : "transparent",
          color: step.active ? "var(--pc-primary)" : step.completed ? "var(--pc-fg-base)" : step.disabled ? "var(--pc-fg-subtle)" : "var(--pc-fg-muted)",
          border: "none",
          borderRadius: "var(--pc-radius-pill)",
          cursor: isClickable ? "pointer" : "default",
          fontSize: "var(--pc-text-sm)",
          fontWeight: step.active ? "var(--pc-weight-semibold)" : "var(--pc-weight-medium)",
          textAlign: "left",
          transition: "all var(--pc-transition-fast)",
          opacity: step.disabled ? 0.5 : 1,
          position: "relative",
          whiteSpace: "nowrap"
        },
        children: [
          showNumber && /* @__PURE__ */ jsx2(
            "span",
            {
              style: {
                width: "1.25rem",
                height: "1.25rem",
                borderRadius: "var(--pc-radius-full)",
                backgroundColor: step.active ? "var(--pc-primary)" : step.completed ? "var(--pc-success)" : "var(--pc-bg-emphasized)",
                color: step.active || step.completed ? "white" : "var(--pc-fg-muted)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "var(--pc-text-xs)",
                fontWeight: "var(--pc-weight-semibold)",
                flexShrink: 0
              },
              children: step.completed ? /* @__PURE__ */ jsx2(CheckIcon, {}) : index + 1
            }
          ),
          step.icon,
          /* @__PURE__ */ jsx2("span", { children: step.label }),
          step.badge && /* @__PURE__ */ jsx2(
            Badge2,
            {
              variant: step.active ? "primary" : "secondary",
              style: { fontSize: "0.625rem", marginLeft: "0.25rem" },
              children: step.badge
            }
          )
        ]
      }
    ),
    showConnector && !isLast && /* @__PURE__ */ jsx2(
      "div",
      {
        style: {
          width: "1.5rem",
          height: "1px",
          backgroundColor: step.completed ? "var(--pc-primary)" : "var(--pc-border)",
          flexShrink: 0
        }
      }
    )
  ] });
}
function WorkflowUserMenu({ user }) {
  const [isOpen, setIsOpen] = useState2(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);
  const handleMenuItemClick = (onClick) => {
    onClick?.();
    setIsOpen(false);
  };
  return /* @__PURE__ */ jsxs2("div", { ref: menuRef, style: { position: "relative" }, children: [
    /* @__PURE__ */ jsxs2(
      "button",
      {
        type: "button",
        onClick: () => setIsOpen(!isOpen),
        style: {
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "0.25rem 0.5rem",
          backgroundColor: "transparent",
          border: "1px solid var(--pc-border)",
          borderRadius: "var(--pc-radius-full)",
          cursor: "pointer"
        },
        children: [
          /* @__PURE__ */ jsx2(
            "div",
            {
              style: {
                width: "2rem",
                height: "2rem",
                borderRadius: "var(--pc-radius-full)",
                backgroundColor: "var(--pc-primary-subtle)",
                color: "var(--pc-primary)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "var(--pc-text-sm)",
                fontWeight: "var(--pc-weight-semibold)"
              },
              children: user.avatar ? /* @__PURE__ */ jsx2(
                "img",
                {
                  src: user.avatar,
                  alt: user.name,
                  style: { width: "100%", height: "100%", borderRadius: "var(--pc-radius-full)" }
                }
              ) : user.name.charAt(0).toUpperCase()
            }
          ),
          /* @__PURE__ */ jsx2(ChevronDownIcon2, {})
        ]
      }
    ),
    isOpen && /* @__PURE__ */ jsxs2(
      "div",
      {
        style: {
          position: "absolute",
          top: "100%",
          right: 0,
          marginTop: "0.25rem",
          minWidth: "200px",
          backgroundColor: "var(--pc-bg-base)",
          border: "1px solid var(--pc-border)",
          borderRadius: "var(--pc-radius-card)",
          boxShadow: "var(--pc-shadow-dropdown)",
          zIndex: 1e3,
          padding: "0.25rem"
        },
        children: [
          /* @__PURE__ */ jsxs2("div", { style: { padding: "0.5rem 0.75rem", borderBottom: "1px solid var(--pc-border)" }, children: [
            /* @__PURE__ */ jsx2("div", { style: { fontWeight: "var(--pc-weight-medium)", color: "var(--pc-fg-base)" }, children: user.name }),
            user.email && /* @__PURE__ */ jsx2("div", { style: { fontSize: "var(--pc-text-xs)", color: "var(--pc-fg-muted)" }, children: user.email }),
            user.viewingAs && /* @__PURE__ */ jsxs2(
              "div",
              {
                style: {
                  marginTop: "0.5rem",
                  padding: "0.25rem 0.5rem",
                  backgroundColor: "var(--pc-warning-subtle)",
                  borderRadius: "var(--pc-radius-sm)",
                  fontSize: "var(--pc-text-xs)",
                  color: "var(--pc-warning)"
                },
                children: [
                  "Viewing as: ",
                  user.viewingAs.label
                ]
              }
            )
          ] }),
          user.isAdmin && user.viewAsOptions && user.viewAsOptions.length > 0 && /* @__PURE__ */ jsxs2(Fragment2, { children: [
            /* @__PURE__ */ jsx2(
              "div",
              {
                style: {
                  padding: "0.5rem 0.75rem",
                  fontSize: "var(--pc-text-xs)",
                  fontWeight: "var(--pc-weight-semibold)",
                  color: "var(--pc-fg-muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  backgroundColor: "var(--pc-bg-subtle)",
                  marginTop: "0.25rem"
                },
                children: "\u{1F441} View as"
              }
            ),
            user.viewingAs && /* @__PURE__ */ jsx2(
              "button",
              {
                type: "button",
                onClick: () => handleMenuItemClick(() => user.onViewAsChange?.(null)),
                style: {
                  width: "100%",
                  padding: "0.5rem 0.75rem",
                  backgroundColor: "transparent",
                  border: "none",
                  borderRadius: "var(--pc-radius-btn)",
                  cursor: "pointer",
                  textAlign: "left",
                  fontSize: "var(--pc-text-sm)",
                  color: "var(--pc-primary)",
                  fontWeight: "var(--pc-weight-medium)"
                },
                children: "\u2190 Return to admin view"
              }
            ),
            user.viewAsOptions.slice(0, 5).map((option) => /* @__PURE__ */ jsxs2(
              "button",
              {
                type: "button",
                onClick: () => handleMenuItemClick(() => user.onViewAsChange?.(option)),
                style: {
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  padding: "0.5rem 0.75rem",
                  backgroundColor: user.viewingAs?.id === option.id ? "var(--pc-primary-subtle)" : "transparent",
                  border: "none",
                  borderRadius: "var(--pc-radius-btn)",
                  cursor: "pointer",
                  textAlign: "left"
                },
                children: [
                  /* @__PURE__ */ jsx2(
                    "div",
                    {
                      style: {
                        width: "1.5rem",
                        height: "1.5rem",
                        borderRadius: "var(--pc-radius-sm)",
                        backgroundColor: option.type === "brand" ? "var(--pc-primary-subtle)" : "var(--pc-secondary-subtle)",
                        color: option.type === "brand" ? "var(--pc-primary)" : "var(--pc-secondary)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "var(--pc-text-xs)",
                        fontWeight: "var(--pc-weight-semibold)",
                        flexShrink: 0
                      },
                      children: option.type === "brand" ? "B" : option.type === "user" ? "U" : "A"
                    }
                  ),
                  /* @__PURE__ */ jsxs2("div", { style: { flex: 1 }, children: [
                    /* @__PURE__ */ jsx2("div", { style: { fontSize: "var(--pc-text-sm)", color: "var(--pc-fg-base)" }, children: option.label }),
                    option.description && /* @__PURE__ */ jsx2("div", { style: { fontSize: "var(--pc-text-xs)", color: "var(--pc-fg-muted)" }, children: option.description })
                  ] })
                ]
              },
              option.id
            )),
            /* @__PURE__ */ jsx2("div", { style: { height: "1px", backgroundColor: "var(--pc-border)", margin: "0.25rem 0" } })
          ] }),
          user.menuItems?.map((menuItem, index) => /* @__PURE__ */ jsx2(
            "button",
            {
              type: "button",
              onClick: () => handleMenuItemClick(menuItem.onClick),
              style: {
                width: "100%",
                padding: "0.5rem 0.75rem",
                backgroundColor: "transparent",
                border: "none",
                borderRadius: "var(--pc-radius-btn)",
                cursor: "pointer",
                textAlign: "left",
                fontSize: "var(--pc-text-sm)",
                color: menuItem.destructive ? "var(--pc-error)" : "var(--pc-fg-base)"
              },
              children: menuItem.label
            },
            index
          ))
        ]
      }
    )
  ] });
}
function AdminIcon() {
  return /* @__PURE__ */ jsxs2(
    "svg",
    {
      width: "16",
      height: "16",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      children: [
        /* @__PURE__ */ jsx2("path", { d: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" }),
        /* @__PURE__ */ jsx2("path", { d: "M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1Z" })
      ]
    }
  );
}
function CheckIcon() {
  return /* @__PURE__ */ jsx2(
    "svg",
    {
      width: "12",
      height: "12",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "3",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      children: /* @__PURE__ */ jsx2("polyline", { points: "20 6 9 17 4 12" })
    }
  );
}
function ChevronDownIcon2() {
  return /* @__PURE__ */ jsx2(
    "svg",
    {
      width: "16",
      height: "16",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      children: /* @__PURE__ */ jsx2("path", { d: "M6 9l6 6 6-6" })
    }
  );
}

// src/blocks/AuthScreen.tsx
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  FormError,
  FormField,
  FormLabel,
  Input
} from "@powercore/ui";
import { useState as useState3 } from "react";
import { Fragment as Fragment3, jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
function AuthScreen({
  mode = "login",
  logo,
  title,
  description,
  onSubmit,
  onModeChange,
  socialProviders = [],
  showRememberMe = true,
  loading = false,
  error,
  footer
}) {
  const [formData, setFormData] = useState3({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    rememberMe: false
  });
  const [validationErrors, setValidationErrors] = useState3({});
  const defaultTitles = {
    login: "Welcome back",
    register: "Create an account",
    "forgot-password": "Reset your password"
  };
  const defaultDescriptions = {
    login: "Enter your credentials to access your account",
    register: "Fill in your details to get started",
    "forgot-password": "Enter your email and we'll send you a reset link"
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!formData.email) errors.email = "Email is required";
    if (mode !== "forgot-password" && !formData.password) errors.password = "Password is required";
    if (mode === "register" && !formData.name) errors.name = "Name is required";
    if (mode === "register" && formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    await onSubmit?.(formData);
  };
  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setValidationErrors((prev) => ({ ...prev, [field]: void 0 }));
  };
  return /* @__PURE__ */ jsx3(
    "div",
    {
      className: "pc-auth-screen",
      style: {
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--pc-bg-subtle)",
        padding: "1rem"
      },
      children: /* @__PURE__ */ jsxs3("div", { style: { width: "100%", maxWidth: "24rem" }, children: [
        logo && /* @__PURE__ */ jsx3("div", { style: { textAlign: "center", marginBottom: "2rem" }, children: logo }),
        /* @__PURE__ */ jsxs3(Card, { children: [
          /* @__PURE__ */ jsxs3(CardHeader, { style: { textAlign: "center" }, children: [
            /* @__PURE__ */ jsx3(CardTitle, { as: "h1", children: title || defaultTitles[mode] }),
            /* @__PURE__ */ jsx3(CardDescription, { children: description || defaultDescriptions[mode] })
          ] }),
          /* @__PURE__ */ jsxs3(CardContent, { children: [
            socialProviders.length > 0 && mode !== "forgot-password" && /* @__PURE__ */ jsxs3(Fragment3, { children: [
              /* @__PURE__ */ jsx3(
                "div",
                {
                  style: {
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                    marginBottom: "1.5rem"
                  },
                  children: socialProviders.map((provider) => /* @__PURE__ */ jsxs3(
                    Button,
                    {
                      variant: "outline",
                      onClick: provider.onClick,
                      style: { width: "100%" },
                      leftIcon: provider.icon,
                      children: [
                        "Continue with ",
                        provider.name
                      ]
                    },
                    provider.id
                  ))
                }
              ),
              /* @__PURE__ */ jsxs3(
                "div",
                {
                  style: {
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                    marginBottom: "1.5rem"
                  },
                  children: [
                    /* @__PURE__ */ jsx3("div", { style: { flex: 1, height: "1px", backgroundColor: "var(--pc-border)" } }),
                    /* @__PURE__ */ jsx3("span", { style: { fontSize: "var(--pc-text-xs)", color: "var(--pc-fg-muted)" }, children: "or continue with email" }),
                    /* @__PURE__ */ jsx3("div", { style: { flex: 1, height: "1px", backgroundColor: "var(--pc-border)" } })
                  ]
                }
              )
            ] }),
            error && /* @__PURE__ */ jsx3(
              "div",
              {
                style: {
                  padding: "0.75rem",
                  backgroundColor: "var(--pc-error-subtle)",
                  borderRadius: "var(--pc-radius-btn)",
                  marginBottom: "1rem",
                  color: "var(--pc-error)",
                  fontSize: "var(--pc-text-sm)"
                },
                children: error
              }
            ),
            /* @__PURE__ */ jsxs3(
              "form",
              {
                onSubmit: handleSubmit,
                style: { display: "flex", flexDirection: "column", gap: "1rem" },
                children: [
                  mode === "register" && /* @__PURE__ */ jsxs3(FormField, { children: [
                    /* @__PURE__ */ jsx3(FormLabel, { htmlFor: "name", required: true, children: "Name" }),
                    /* @__PURE__ */ jsx3(
                      Input,
                      {
                        id: "name",
                        type: "text",
                        placeholder: "John Doe",
                        value: formData.name,
                        onChange: (e) => updateField("name", e.target.value),
                        error: !!validationErrors.name,
                        disabled: loading
                      }
                    ),
                    /* @__PURE__ */ jsx3(FormError, { children: validationErrors.name })
                  ] }),
                  /* @__PURE__ */ jsxs3(FormField, { children: [
                    /* @__PURE__ */ jsx3(FormLabel, { htmlFor: "email", required: true, children: "Email" }),
                    /* @__PURE__ */ jsx3(
                      Input,
                      {
                        id: "email",
                        type: "email",
                        placeholder: "you@example.com",
                        value: formData.email,
                        onChange: (e) => updateField("email", e.target.value),
                        error: !!validationErrors.email,
                        disabled: loading
                      }
                    ),
                    /* @__PURE__ */ jsx3(FormError, { children: validationErrors.email })
                  ] }),
                  mode !== "forgot-password" && /* @__PURE__ */ jsxs3(FormField, { children: [
                    /* @__PURE__ */ jsx3(FormLabel, { htmlFor: "password", required: true, children: "Password" }),
                    /* @__PURE__ */ jsx3(
                      Input,
                      {
                        id: "password",
                        type: "password",
                        placeholder: "Enter your password",
                        value: formData.password,
                        onChange: (e) => updateField("password", e.target.value),
                        error: !!validationErrors.password,
                        disabled: loading
                      }
                    ),
                    /* @__PURE__ */ jsx3(FormError, { children: validationErrors.password })
                  ] }),
                  mode === "register" && /* @__PURE__ */ jsxs3(FormField, { children: [
                    /* @__PURE__ */ jsx3(FormLabel, { htmlFor: "confirmPassword", required: true, children: "Confirm Password" }),
                    /* @__PURE__ */ jsx3(
                      Input,
                      {
                        id: "confirmPassword",
                        type: "password",
                        placeholder: "Confirm your password",
                        value: formData.confirmPassword,
                        onChange: (e) => updateField("confirmPassword", e.target.value),
                        error: !!validationErrors.confirmPassword,
                        disabled: loading
                      }
                    ),
                    /* @__PURE__ */ jsx3(FormError, { children: validationErrors.confirmPassword })
                  ] }),
                  mode === "login" && /* @__PURE__ */ jsxs3(
                    "div",
                    {
                      style: { display: "flex", justifyContent: "space-between", alignItems: "center" },
                      children: [
                        showRememberMe && /* @__PURE__ */ jsx3(
                          Checkbox,
                          {
                            id: "rememberMe",
                            label: "Remember me",
                            checked: formData.rememberMe,
                            onChange: (e) => updateField("rememberMe", e.target.checked),
                            disabled: loading
                          }
                        ),
                        /* @__PURE__ */ jsx3(
                          "button",
                          {
                            type: "button",
                            onClick: () => onModeChange?.("forgot-password"),
                            style: {
                              background: "none",
                              border: "none",
                              color: "var(--pc-primary)",
                              fontSize: "var(--pc-text-sm)",
                              cursor: "pointer"
                            },
                            children: "Forgot password?"
                          }
                        )
                      ]
                    }
                  ),
                  /* @__PURE__ */ jsxs3(Button, { type: "submit", loading, style: { width: "100%" }, children: [
                    mode === "login" && "Sign in",
                    mode === "register" && "Create account",
                    mode === "forgot-password" && "Send reset link"
                  ] })
                ]
              }
            )
          ] }),
          /* @__PURE__ */ jsxs3(CardFooter, { style: { justifyContent: "center" }, children: [
            mode === "login" && /* @__PURE__ */ jsxs3("p", { style: { fontSize: "var(--pc-text-sm)", color: "var(--pc-fg-muted)" }, children: [
              "Don't have an account?",
              " ",
              /* @__PURE__ */ jsx3(
                "button",
                {
                  type: "button",
                  onClick: () => onModeChange?.("register"),
                  style: {
                    background: "none",
                    border: "none",
                    color: "var(--pc-primary)",
                    cursor: "pointer",
                    fontWeight: "var(--pc-weight-medium)"
                  },
                  children: "Sign up"
                }
              )
            ] }),
            mode === "register" && /* @__PURE__ */ jsxs3("p", { style: { fontSize: "var(--pc-text-sm)", color: "var(--pc-fg-muted)" }, children: [
              "Already have an account?",
              " ",
              /* @__PURE__ */ jsx3(
                "button",
                {
                  type: "button",
                  onClick: () => onModeChange?.("login"),
                  style: {
                    background: "none",
                    border: "none",
                    color: "var(--pc-primary)",
                    cursor: "pointer",
                    fontWeight: "var(--pc-weight-medium)"
                  },
                  children: "Sign in"
                }
              )
            ] }),
            mode === "forgot-password" && /* @__PURE__ */ jsxs3("p", { style: { fontSize: "var(--pc-text-sm)", color: "var(--pc-fg-muted)" }, children: [
              "Remember your password?",
              " ",
              /* @__PURE__ */ jsx3(
                "button",
                {
                  type: "button",
                  onClick: () => onModeChange?.("login"),
                  style: {
                    background: "none",
                    border: "none",
                    color: "var(--pc-primary)",
                    cursor: "pointer",
                    fontWeight: "var(--pc-weight-medium)"
                  },
                  children: "Sign in"
                }
              )
            ] })
          ] })
        ] }),
        footer && /* @__PURE__ */ jsx3("div", { style: { textAlign: "center", marginTop: "1.5rem" }, children: footer })
      ] })
    }
  );
}

// src/blocks/SettingsForm.tsx
import {
  Button as Button2,
  Card as Card2,
  CardContent as CardContent2,
  CardDescription as CardDescription2,
  CardFooter as CardFooter2,
  CardHeader as CardHeader2,
  CardTitle as CardTitle2,
  FormDescription,
  FormError as FormError2,
  FormField as FormField2,
  FormLabel as FormLabel2,
  Input as Input2,
  Select,
  Switch,
  Textarea
} from "@powercore/ui";
import { useState as useState4 } from "react";
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
function SettingsForm({
  sections,
  values,
  onChange,
  onSubmit,
  onCancel,
  loading = false,
  errors = {},
  submitLabel = "Save changes",
  cancelLabel = "Cancel",
  showCancel = true
}) {
  const [localValues, setLocalValues] = useState4(values);
  const [isDirty, setIsDirty] = useState4(false);
  const currentValues = onChange ? values : localValues;
  const updateValue = (fieldId, value) => {
    if (onChange) {
      onChange({ ...currentValues, [fieldId]: value });
    } else {
      setLocalValues((prev) => ({ ...prev, [fieldId]: value }));
    }
    setIsDirty(true);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSubmit?.(currentValues);
    setIsDirty(false);
  };
  const handleCancel = () => {
    if (!onChange) {
      setLocalValues(values);
    }
    setIsDirty(false);
    onCancel?.();
  };
  return /* @__PURE__ */ jsx4("form", { onSubmit: handleSubmit, className: "pc-settings-form", children: /* @__PURE__ */ jsxs4("div", { style: { display: "flex", flexDirection: "column", gap: "1.5rem" }, children: [
    sections.map((section) => /* @__PURE__ */ jsxs4(Card2, { children: [
      /* @__PURE__ */ jsxs4(CardHeader2, { children: [
        /* @__PURE__ */ jsx4(CardTitle2, { children: section.title }),
        section.description && /* @__PURE__ */ jsx4(CardDescription2, { children: section.description })
      ] }),
      /* @__PURE__ */ jsx4(CardContent2, { children: /* @__PURE__ */ jsx4("div", { style: { display: "flex", flexDirection: "column", gap: "1.25rem" }, children: section.fields.map((field) => /* @__PURE__ */ jsx4(
        SettingFieldRenderer,
        {
          field,
          value: currentValues[field.id],
          onChange: (value) => updateValue(field.id, value),
          error: errors[field.id],
          disabled: loading || field.disabled
        },
        field.id
      )) }) })
    ] }, section.id)),
    /* @__PURE__ */ jsxs4(
      "div",
      {
        style: {
          display: "flex",
          justifyContent: "flex-end",
          gap: "0.75rem",
          paddingTop: "0.5rem"
        },
        children: [
          showCancel && /* @__PURE__ */ jsx4(Button2, { type: "button", variant: "outline", onClick: handleCancel, disabled: loading, children: cancelLabel }),
          /* @__PURE__ */ jsx4(Button2, { type: "submit", loading, disabled: !isDirty && !loading, children: submitLabel })
        ]
      }
    )
  ] }) });
}
function SettingFieldRenderer({
  field,
  value,
  onChange,
  error,
  disabled
}) {
  const renderField = () => {
    switch (field.type) {
      case "text":
      case "email":
      case "password":
        return /* @__PURE__ */ jsx4(
          Input2,
          {
            id: field.id,
            type: field.type,
            placeholder: field.placeholder,
            value: value || "",
            onChange: (e) => onChange(e.target.value),
            error: !!error,
            disabled
          }
        );
      case "textarea":
        return /* @__PURE__ */ jsx4(
          Textarea,
          {
            id: field.id,
            placeholder: field.placeholder,
            value: value || "",
            onChange: (e) => onChange(e.target.value),
            error: !!error,
            disabled,
            style: { minHeight: "100px" }
          }
        );
      case "select":
        return /* @__PURE__ */ jsxs4(
          Select,
          {
            id: field.id,
            value: value || "",
            onChange: (e) => onChange(e.target.value),
            error: !!error,
            disabled,
            children: [
              /* @__PURE__ */ jsx4("option", { value: "", children: "Select an option" }),
              field.options?.map((option) => /* @__PURE__ */ jsx4("option", { value: option.value, children: option.label }, option.value))
            ]
          }
        );
      case "switch":
        return /* @__PURE__ */ jsxs4("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between" }, children: [
          /* @__PURE__ */ jsxs4("div", { children: [
            /* @__PURE__ */ jsx4(
              "div",
              {
                style: {
                  fontWeight: "var(--pc-weight-medium)",
                  color: "var(--pc-fg-base)",
                  fontSize: "var(--pc-text-sm)"
                },
                children: field.label
              }
            ),
            field.description && /* @__PURE__ */ jsx4(
              "div",
              {
                style: {
                  fontSize: "var(--pc-text-xs)",
                  color: "var(--pc-fg-muted)",
                  marginTop: "0.125rem"
                },
                children: field.description
              }
            )
          ] }),
          /* @__PURE__ */ jsx4(
            Switch,
            {
              id: field.id,
              checked: !!value,
              onChange: (e) => onChange(e.target.checked),
              disabled
            }
          )
        ] });
      case "custom":
        return field.render?.({ value, onChange });
      default:
        return null;
    }
  };
  if (field.type === "switch") {
    return /* @__PURE__ */ jsxs4("div", { style: { paddingTop: "0.25rem", paddingBottom: "0.25rem" }, children: [
      renderField(),
      error && /* @__PURE__ */ jsx4(FormError2, { children: error })
    ] });
  }
  return /* @__PURE__ */ jsxs4(FormField2, { children: [
    /* @__PURE__ */ jsx4(FormLabel2, { htmlFor: field.id, required: field.required, children: field.label }),
    field.description && /* @__PURE__ */ jsx4(FormDescription, { children: field.description }),
    renderField(),
    /* @__PURE__ */ jsx4(FormError2, { children: error })
  ] });
}
function SettingsSection({ title, description, children, actions }) {
  return /* @__PURE__ */ jsxs4(Card2, { children: [
    /* @__PURE__ */ jsxs4(CardHeader2, { children: [
      /* @__PURE__ */ jsx4(CardTitle2, { children: title }),
      description && /* @__PURE__ */ jsx4(CardDescription2, { children: description })
    ] }),
    /* @__PURE__ */ jsx4(CardContent2, { children }),
    actions && /* @__PURE__ */ jsx4(CardFooter2, { style: { justifyContent: "flex-end", gap: "0.75rem" }, children: actions })
  ] });
}

// src/blocks/FileUploadSection.tsx
import {
  Badge as Badge3,
  Button as Button3,
  Card as Card3,
  CardContent as CardContent3,
  CardDescription as CardDescription3,
  CardHeader as CardHeader3,
  CardTitle as CardTitle3
} from "@powercore/ui";
import { useCallback, useRef as useRef2, useState as useState5 } from "react";
import { Fragment as Fragment4, jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
function FileUploadSection({
  title = "Upload files",
  description = "Drag and drop files here, or click to select",
  accept,
  multiple = true,
  maxSize = 10 * 1024 * 1024,
  // 10MB default
  maxFiles = 10,
  files = [],
  onFilesChange,
  onUpload,
  onRemove,
  disabled = false
}) {
  const [isDragOver, setIsDragOver] = useState5(false);
  const [localFiles, setLocalFiles] = useState5(files);
  const inputRef = useRef2(null);
  const currentFiles = onFilesChange ? files : localFiles;
  const updateFiles = useCallback(
    (newFiles) => {
      if (onFilesChange) {
        onFilesChange(newFiles);
      } else {
        setLocalFiles(newFiles);
      }
    },
    [onFilesChange]
  );
  const validateFile = (file) => {
    if (maxSize && file.size > maxSize) {
      return `File size exceeds ${formatFileSize(maxSize)}`;
    }
    if (accept) {
      const acceptedTypes = accept.split(",").map((t) => t.trim());
      const isValidType = acceptedTypes.some((type) => {
        if (type.startsWith(".")) {
          return file.name.toLowerCase().endsWith(type.toLowerCase());
        }
        if (type.endsWith("/*")) {
          return file.type.startsWith(type.slice(0, -1));
        }
        return file.type === type;
      });
      if (!isValidType) {
        return `File type not accepted`;
      }
    }
    return null;
  };
  const addFiles = useCallback(
    async (fileList) => {
      if (!fileList || disabled) return;
      const newFiles = [];
      const filesToUpload = [];
      for (let i = 0; i < fileList.length; i++) {
        if (!multiple && currentFiles.length + newFiles.length >= 1) break;
        if (maxFiles && currentFiles.length + newFiles.length >= maxFiles) break;
        const file = fileList[i];
        const error = validateFile(file);
        const uploadedFile = {
          id: `file-${Date.now()}-${i}`,
          name: file.name,
          size: file.size,
          type: file.type,
          status: error ? "error" : "pending",
          error: error || void 0,
          file
        };
        newFiles.push(uploadedFile);
        if (!error) {
          filesToUpload.push(file);
        }
      }
      updateFiles([...currentFiles, ...newFiles]);
      if (filesToUpload.length > 0 && onUpload) {
        await onUpload(filesToUpload);
      }
    },
    [currentFiles, disabled, maxFiles, multiple, onUpload, updateFiles, validateFile]
  );
  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    addFiles(e.dataTransfer.files);
  };
  const handleInputChange = (e) => {
    addFiles(e.target.files);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };
  const handleRemove = (fileId) => {
    updateFiles(currentFiles.filter((f) => f.id !== fileId));
    onRemove?.(fileId);
  };
  const openFilePicker = () => {
    if (!disabled) {
      inputRef.current?.click();
    }
  };
  return /* @__PURE__ */ jsxs5(Card3, { className: "pc-file-upload-section", children: [
    /* @__PURE__ */ jsxs5(CardHeader3, { children: [
      /* @__PURE__ */ jsx5(CardTitle3, { children: title }),
      /* @__PURE__ */ jsx5(CardDescription3, { children: description })
    ] }),
    /* @__PURE__ */ jsxs5(CardContent3, { children: [
      /* @__PURE__ */ jsxs5(
        "div",
        {
          onClick: openFilePicker,
          onDragOver: handleDragOver,
          onDragLeave: handleDragLeave,
          onDrop: handleDrop,
          style: {
            border: `2px dashed ${isDragOver ? "var(--pc-primary)" : "var(--pc-border)"}`,
            borderRadius: "var(--pc-radius-card)",
            padding: "2rem",
            textAlign: "center",
            backgroundColor: isDragOver ? "var(--pc-primary-subtle)" : "var(--pc-bg-subtle)",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.5 : 1,
            transition: "all var(--pc-transition-fast)"
          },
          children: [
            /* @__PURE__ */ jsx5(UploadIcon, {}),
            /* @__PURE__ */ jsx5(
              "p",
              {
                style: {
                  marginTop: "0.75rem",
                  color: "var(--pc-fg-base)",
                  fontWeight: "var(--pc-weight-medium)"
                },
                children: "Drop files here or click to browse"
              }
            ),
            /* @__PURE__ */ jsxs5(
              "p",
              {
                style: {
                  marginTop: "0.25rem",
                  fontSize: "var(--pc-text-xs)",
                  color: "var(--pc-fg-muted)"
                },
                children: [
                  accept ? `Accepted: ${accept}` : "All file types accepted",
                  maxSize && ` \u2022 Max ${formatFileSize(maxSize)}`
                ]
              }
            )
          ]
        }
      ),
      /* @__PURE__ */ jsx5(
        "input",
        {
          ref: inputRef,
          type: "file",
          accept,
          multiple,
          onChange: handleInputChange,
          disabled,
          style: { display: "none" }
        }
      ),
      currentFiles.length > 0 && /* @__PURE__ */ jsx5(
        "div",
        {
          style: { marginTop: "1rem", display: "flex", flexDirection: "column", gap: "0.5rem" },
          children: currentFiles.map((file) => /* @__PURE__ */ jsx5(FileItem, { file, onRemove: () => handleRemove(file.id) }, file.id))
        }
      )
    ] })
  ] });
}
function FileItem({ file, onRemove }) {
  const statusColors = {
    pending: "default",
    uploading: "info",
    complete: "success",
    error: "error"
  };
  return /* @__PURE__ */ jsxs5(
    "div",
    {
      style: {
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        padding: "0.75rem",
        backgroundColor: "var(--pc-bg-base)",
        border: "1px solid var(--pc-border)",
        borderRadius: "var(--pc-radius-btn)"
      },
      children: [
        /* @__PURE__ */ jsx5(FileIcon, { type: file.type }),
        /* @__PURE__ */ jsxs5("div", { style: { flex: 1, minWidth: 0 }, children: [
          /* @__PURE__ */ jsxs5(
            "div",
            {
              style: {
                display: "flex",
                alignItems: "center",
                gap: "0.5rem"
              },
              children: [
                /* @__PURE__ */ jsx5(
                  "span",
                  {
                    style: {
                      fontWeight: "var(--pc-weight-medium)",
                      color: "var(--pc-fg-base)",
                      fontSize: "var(--pc-text-sm)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    },
                    children: file.name
                  }
                ),
                /* @__PURE__ */ jsx5(Badge3, { variant: statusColors[file.status], children: file.status })
              ]
            }
          ),
          /* @__PURE__ */ jsxs5("div", { style: { fontSize: "var(--pc-text-xs)", color: "var(--pc-fg-muted)" }, children: [
            formatFileSize(file.size),
            file.error && /* @__PURE__ */ jsx5("span", { style: { color: "var(--pc-error)", marginLeft: "0.5rem" }, children: file.error })
          ] }),
          file.status === "uploading" && file.progress !== void 0 && /* @__PURE__ */ jsx5(
            "div",
            {
              style: {
                marginTop: "0.5rem",
                height: "4px",
                backgroundColor: "var(--pc-bg-muted)",
                borderRadius: "var(--pc-radius-full)",
                overflow: "hidden"
              },
              children: /* @__PURE__ */ jsx5(
                "div",
                {
                  style: {
                    width: `${file.progress}%`,
                    height: "100%",
                    backgroundColor: "var(--pc-primary)",
                    transition: "width var(--pc-transition-fast)"
                  }
                }
              )
            }
          )
        ] }),
        /* @__PURE__ */ jsx5(
          Button3,
          {
            variant: "ghost",
            size: "icon",
            onClick: onRemove,
            "aria-label": "Remove file",
            style: { flexShrink: 0 },
            children: /* @__PURE__ */ jsx5(CloseIcon, {})
          }
        )
      ]
    }
  );
}
function formatFileSize(bytes) {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
function UploadIcon() {
  return /* @__PURE__ */ jsxs5(
    "svg",
    {
      width: "40",
      height: "40",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "var(--pc-fg-muted)",
      strokeWidth: "1.5",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      style: { margin: "0 auto" },
      children: [
        /* @__PURE__ */ jsx5("path", { d: "M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" }),
        /* @__PURE__ */ jsx5("polyline", { points: "17 8 12 3 7 8" }),
        /* @__PURE__ */ jsx5("line", { x1: "12", y1: "3", x2: "12", y2: "15" })
      ]
    }
  );
}
function FileIcon({ type }) {
  const isImage = type.startsWith("image/");
  const isPdf = type === "application/pdf";
  return /* @__PURE__ */ jsx5(
    "div",
    {
      style: {
        width: "2.5rem",
        height: "2.5rem",
        borderRadius: "var(--pc-radius-btn)",
        backgroundColor: isImage ? "var(--pc-success-subtle)" : isPdf ? "var(--pc-error-subtle)" : "var(--pc-bg-muted)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0
      },
      children: /* @__PURE__ */ jsx5(
        "svg",
        {
          width: "20",
          height: "20",
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: isImage ? "var(--pc-success)" : isPdf ? "var(--pc-error)" : "var(--pc-fg-muted)",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          children: isImage ? /* @__PURE__ */ jsxs5(Fragment4, { children: [
            /* @__PURE__ */ jsx5("rect", { x: "3", y: "3", width: "18", height: "18", rx: "2", ry: "2" }),
            /* @__PURE__ */ jsx5("circle", { cx: "8.5", cy: "8.5", r: "1.5" }),
            /* @__PURE__ */ jsx5("polyline", { points: "21 15 16 10 5 21" })
          ] }) : /* @__PURE__ */ jsxs5(Fragment4, { children: [
            /* @__PURE__ */ jsx5("path", { d: "M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" }),
            /* @__PURE__ */ jsx5("polyline", { points: "14 2 14 8 20 8" })
          ] })
        }
      )
    }
  );
}
function CloseIcon() {
  return /* @__PURE__ */ jsx5(
    "svg",
    {
      width: "16",
      height: "16",
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2",
      children: /* @__PURE__ */ jsx5("path", { d: "M18 6L6 18M6 6l12 12" })
    }
  );
}

// src/index.ts
var UI_BLOCKS_VERSION = "0.0.1";
export {
  AuthScreen,
  DashboardShell,
  FileUploadSection,
  SettingsForm,
  SettingsSection,
  UI_BLOCKS_VERSION,
  WorkflowShell
};
